var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { reportServerReachable, reportServerUnreachable } from '@/lib/connection-status';
const KIND = '__apiErrorKind';
const STATUS = '__apiErrorStatus';
const GENERIC = '__apiErrorGeneric';
function tag(target, key, value) {
    Object.defineProperty(target, key, { value, enumerable: false, configurable: true });
}
/** Marks a body built by the client itself (statusText, not something the server said). */
export function markGeneric(body) {
    if (body && typeof body === 'object')
        tag(body, GENERIC, true);
    return body;
}
/** Tags an HTTP error body with the response status. */
export function withStatus(body, response) {
    if (body && typeof body === 'object') {
        tag(body, KIND, 'http');
        tag(body, STATUS, response.status);
    }
    return body;
}
/** The rejection for an error response with no JSON body. Same shape as before. */
export function statusFailure(response, method, parsed) {
    return withStatus(markGeneric({
        error: `${method} request failed: ${response.statusText}`,
        details: parsed || '',
    }), response);
}
// fetch() rejections from send(): the request never got a response.
const transportFailures = new WeakSet();
function isAbort(err) {
    return err instanceof DOMException && err.name === 'AbortError';
}
/**
 * fetch(), plus connection reporting (VA-29259): any response at all proves the
 * server is reachable; a rejected fetch that wasn't an abort means it isn't.
 */
export function send(input, init) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            const response = yield fetch(input, init);
            reportServerReachable();
            return response;
        }
        catch (err) {
            if (!isAbort(err))
                reportServerUnreachable();
            if (err && typeof err === 'object')
                transportFailures.add(err);
            throw err;
        }
    });
}
/** The rejection for a request that never got a usable response. Same shape as before. */
export function requestFailed(err) {
    const message = err instanceof Error ? err.message : undefined;
    const body = { error: 'Request failed', details: message || String(err) };
    if (isAbort(err))
        tag(body, KIND, 'aborted');
    else if (err && typeof err === 'object' && transportFailures.has(err))
        tag(body, KIND, 'network');
    markGeneric(body);
    return body;
}
export function getApiErrorKind(err) {
    return err && typeof err === 'object' ? err[KIND] : undefined;
}
/** True for a request that never reached the server, whether the client wrapped it or not. */
export function isNetworkFailure(err) {
    if (getApiErrorKind(err) === 'network')
        return true;
    return !!(err && typeof err === 'object' && transportFailures.has(err));
}
export function getApiErrorStatus(err) {
    return err && typeof err === 'object' ? err[STATUS] : undefined;
}
export function isGenericApiError(err) {
    return !!(err && typeof err === 'object' && err[GENERIC]);
}
export const NETWORK_ERROR_MESSAGE = "We couldn't reach Vriti. Check your connection and try again.";
/** Plain-language copy for a status, when the server gave nothing better. 401 is the session guard's job. */
export function messageForStatus(status) {
    if (!status)
        return undefined;
    if (status === 403)
        return "You don't have permission to do this. Ask your organization admin.";
    if (status === 404)
        return 'This item no longer exists or was removed.';
    if (status === 429)
        return 'Too many requests right now. Please wait a moment and try again.';
    if (status >= 500)
        return 'Something went wrong on our side. Please try again.';
    return undefined;
}
